import { createFileRoute } from "@tanstack/react-router";
import { PHOTOGRAPHER } from "@/content/foto";
import { FotoFilmShowcase } from "@/components/site/foto/FotoFilmShowcase";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { FotoQuoteForm } from "@/components/site/foto/FotoQuoteForm";
import { PhotoMasonry } from "@/components/site/foto/PhotoMasonry";
import { coverPhotoFor, galleryPhotos } from "@/content/photo-gallery";
import {
  aeoGeo,
  breadcrumbs,
  faqPage,
  imageGallery,
  serviceSchema,
  person,
  speakable,
} from "@/lib/seo/schema";

const COVER = coverPhotoFor("foretagsfotografering");

const PATH = "/foto/foretagsfotografering";
const TITLE = "Företagsfotografering — fotograf för företag | Fenny";
const DESCRIPTION =
  "Företagsfotografering med porträtt, miljöbilder, kampanj och event. Enhetligt bildmanér, leverans inom 3–5 arbetsdagar och offert per uppdrag.";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad ingår i företagsfotografering?",
    a: "Ett typiskt uppdrag innehåller porträtt på medarbetare, miljöbilder från arbetsplatsen och några situationsbilder som visar hur ni arbetar. Vi sätter bildmanér i förväg, fotograferar enligt schema och levererar retuscherade bilder i webb- och tryckformat.",
  },
  {
    q: "Vad kostar företagsfotografering?",
    a: "Vi offererar per uppdrag i stället för att sälja paket. Priset styrs av tid på plats, antal färdiga bilder, om vi bygger studioljus och hur bilderna ska användas. Ni får ett fast pris innan dagen bokas — inga rörliga timpriser i efterhand.",
  },
  {
    q: "Hur många bilder får vi?",
    a: "Vid en halvdag levererar vi normalt 20–40 färdiga bilder, vid en heldag 40–80. Antalet sätts i offerten så att ni vet exakt vad ni betalar för.",
  },
  {
    q: "Kan ni fotografera i våra lokaler?",
    a: "Ja. Vi tar med mobilt studioljus och bygger upp en fotoplats i ett konferensrum eller i en del av kontoret. Det behövs ungefär tre gånger tre meter fri yta.",
  },
  {
    q: "Hur ofta bör ett företag uppdatera bilderna?",
    a: "Vartannat till vart tredje år för kampanj- och miljöbilder. Porträtt kompletteras löpande när nya medarbetare börjar — vi sparar ljussättning och manér så att nya bilder matchar de befintliga.",
  },
  {
    q: "Kan vi kombinera fotografering med film?",
    a: "Ja, och det sänker kostnaden per leverans. Eftersom Fenny främst är ett filmproduktionsbolag har vi redan ljus och team på plats; att lägga till stillbilder på samma dag är betydligt billigare än två separata produktioner.",
  },
];

export const Route = createFileRoute("/foto/foretagsfotografering")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Företagsfotografering — Fenny Foto" },
      {
        property: "og:description",
        content: "Porträtt, miljöbilder och kampanj för företag. Offert per uppdrag.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
    ],
    links: [{ rel: "canonical", href: `https://fenny.se${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(aeoGeo({
            path: PATH,
            name: TITLE,
            description: DESCRIPTION,
            question: "Vad ingår i företagsfotografering och vad kostar det?",
            answer:
              "Företagsfotografering hos Fenny innehåller medarbetarporträtt, miljöbilder från arbetsplatsen och situationsbilder som visar hur ni arbetar. En halvdag ger normalt 20–40 färdiga bilder och en heldag 40–80, levererade inom 3–5 arbetsdagar. Priset offereras per uppdrag utifrån tid, antal bilder, ljussättning och användningsrätt. Vi fotograferar i Stockholm, Göteborg, Malmö, Uppsala och Västerås. Kontakt: Fenny, hey@fenny.se.",
            speakableSelectors: ["[data-quick-answer]"],
          })),
      },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Företagsfotografering",
            path: PATH,
            serviceType: "Företagsfotografering",
            description:
              "Fotografering för företag: porträtt, miljöbilder, kampanj och event. Offert per uppdrag.",
            areaServed: ["Stockholm", "Göteborg", "Malmö", "Uppsala", "Västerås", "Sverige"],
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          imageGallery({
            name: "Företagsfotografering — exempel",
            path: PATH,
            description: "Exempel på företagsfotografering av Fenny för Fenny.",
            images: galleryPhotos.slice(0, 16),
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          person({
            name: PHOTOGRAPHER.name,
            jobTitle: PHOTOGRAPHER.role,
            email: PHOTOGRAPHER.email,
            path: PATH,
            description: PHOTOGRAPHER.bio,
            knowsAbout: [
              "Företagsfotografering",
              "Medarbetarporträtt",
              "Miljöbilder",
              "Fotograf Stockholm",
              "Fotograf Göteborg",
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Foto", path: "/foto" },
            { name: "Företagsfotografering", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <LandingPage
      heroImage={{ src: COVER.src, alt: COVER.alt }}
      eyebrow="Företagsfotografering"
      title={<>Företagsfotografering som håller ihop hela varumärket.</>}
      lede="Porträtt, miljöbilder och kampanj i ett och samma bildmanér — fotograferat i era lokaler eller i studio. Vi sätter manéret en gång och håller det över tid, så att sajten inte spretar när nya medarbetare tillkommer."
      tldr={
        <p>
          Företagsfotografering hos Fenny omfattar <strong>porträtt</strong>,{" "}
          <strong>miljöbilder</strong>, <strong>kampanj</strong> och{" "}
          <strong>event</strong>. Leverans inom <strong>3–5 arbetsdagar</strong>,
          fri användningsrätt i egna kanaler och <strong>offert per uppdrag</strong>{" "}
          — vi har inga fasta paketpriser.
        </p>
      }
      ctaPrimary={{ label: "Begär offert", to: "#offert" }}
      ctaSecondary={{ label: "Se portfolion", to: "/foto" }}
      trackingId="foto_foretagsfotografering"
      stills={false}
      videos={false}
      afterHero={
        <div className="py-12">
          <PhotoMasonry initial={9} filterable={false} photos={galleryPhotos.slice(0, 12)} />
        </div>
      }
      bullets={[
        {
          title: "Ett manér, inte en bildkarusell",
          body: "Ljus, bakgrund och beskärning definieras i förväg och dokumenteras, så att nästa fotografering matchar den förra.",
        },
        {
          title: "Schema som håller",
          body: "Vi kör 25–40 porträtt på en dag när schemat är satt. Ingen väntar mer än tio minuter.",
        },
        {
          title: "Fri användningsrätt",
          body: "Bilderna är era att använda i sajt, sociala kanaler, press, rekrytering och tryck.",
        },
      ]}
      process={{
        heading: "Så går ett uppdrag till",
        steps: [
          { label: "Förfrågan", body: "Ni beskriver behovet. Vi svarar med upplägg och fast pris.", duration: "1 arbetsdag" },
          { label: "Bildmanér", body: "Referenser, ljus, bakgrund och lista på vad som ska levereras.", duration: "innan dagen" },
          { label: "Fotografering", body: "Studio eller er arbetsplats. Vi tar med all utrustning.", duration: "halv- eller heldag" },
          { label: "Urval och retusch", body: "Vi gallrar, retuscherar och färgsätter enligt manéret.", duration: "2–4 dagar" },
          { label: "Leverans", body: "Webb- och tryckformat i mapp, med filnamn som går att arbeta i.", duration: "dag 3–5" },
        ],
      }}
      sections={[
        {
          id: "vad-driver-priset",
          heading: "Vad som styr priset",
          body: (
            <>
              <p>
                Vi lämnar alltid offert i stället för listpris, av ett enkelt skäl:
                två uppdrag med samma rubrik kan skilja fem gånger i arbetsinsats.
                Fyra saker avgör: tid på plats, antal färdiga bilder, ljussättning
                (befintligt ljus eller riggad studio) och användningsområde.
              </p>
              <p>
                Läs mer i vår genomgång av{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foto/vad-kostar-en-fotograf">
                  vad en fotograf kostar
                </a>{" "}
                — där bryter vi ned varje kostnadsdrivare.
              </p>
            </>
          ),
        },
        {
          id: "vanliga-upplagg",
          heading: "Tre vanliga upplägg",
          body: (
            <>
              <p>
                <strong>Porträttdag.</strong> Studioljus i ert konferensrum, hela
                organisationen igenom på ett schema. Se{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foto/foretagsportratt">
                  företagsporträtt
                </a>
                .
              </p>
              <p>
                <strong>Varumärkespaket.</strong> Porträtt plus miljö- och
                situationsbilder som täcker sajt, säljmaterial och annons. Se{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foto/kampanjfoto">
                  kampanjfoto
                </a>
                .
              </p>
              <p>
                <strong>Foto plus film samma dag.</strong> En riggning, två
                leveranser. Se{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foto/foto-och-film">
                  foto och film
                </a>
                .
              </p>
            </>
          ),
        },
      ]}
      afterSections={<FotoFilmShowcase offset={8} heading="Företagets bild — film och foto" lede="Porträtt och miljöbilder till sajten, film till kampanj och rekrytering. Producerat i samma manér." />}
      faq={FAQ}
      riskReducers
      closing={{
        title: "Behöver ni nya bilder?",
        body: "Skicka en kort beskrivning av uppdraget så återkommer Fenny med upplägg och fast pris inom en arbetsdag.",
        primary: { label: "Begär offert", to: "#offert" },
      }}
      contactSlot={
        <FotoQuoteForm
          location="foto_foretagsfotografering"
          defaultAssignment="foretagsportratt"
        />
      }
    />
  );
}
